import React, { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

// Import Pages
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import TechnicianDashboard from './pages/TechnicianDashboard';
import Buildings from './pages/Buildings';
import Assets from './pages/Assets';
import Complaints from './pages/Complaints';
import PPMTasks from './pages/PPMTasks';
import Staff from './pages/Staff';
import Suppliers from './pages/Suppliers';
import PurchaseManagement from './pages/PurchaseManagement';
import StockManagement from './pages/StockManagement';
import Tenants from './pages/Tenants';
import Certificates from './pages/Certificates';
import UtilityReadings from './pages/UtilityReadings';
import AIInsights from './pages/AIInsights';
import UserManagement from './pages/UserManagement';
import CompanyManagement from './pages/CompanyManagement';
import Contacts from './pages/Contacts';
import Attendance from './pages/Attendance';
import Schedules from './pages/Schedules';
import Notices from './pages/Notices';
import SOPs from './pages/SOPs';
import ServiceReports from './pages/ServiceReports';
import Approvals from './pages/Approvals';
import PurchaseRequests from './pages/PurchaseRequests';
import Analytics from './pages/Analytics';
import BuildingDetail from './pages/BuildingDetail';
import Settings from './pages/Settings';
import Contact from './pages/Contact';
import Help from './pages/Help';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Subscribe from './pages/Subscribe';
import ChangePassword from './pages/ChangePassword';
import CustomReports from './pages/CustomReports';
import AMCContracts from './pages/AMCContracts';
import ServiceProviderKPI from './pages/ServiceProviderKPI';
import StockMovements from './pages/StockMovements';
import AuditLogs from './pages/AuditLogs';
import Backups from './pages/Backups';
import RoleAccess from './pages/RoleAccess';
import Accounting from './pages/Accounting';
import HR from './pages/HR';
import Onboarding from './pages/Onboarding';
import SuperAdminOnboardCompany from './pages/SuperAdminOnboardCompany';
import PendingReturns from './pages/PendingReturns';
import Subscription from './pages/Subscription';
import DocumentVault from './pages/DocumentVault';
import Workflows from './pages/Workflows';
import PurchaseOrders from './pages/PurchaseOrders';
import SupplierDashboard from './pages/SupplierDashboard';
import AdvancedAccounting from './pages/AdvancedAccounting';
import Quotations from './pages/Quotations';
import Vouchers from './pages/Vouchers';
import PpmCalendar from './pages/PpmCalendar';
import Procurement from './pages/Procurement';
import SalesPurchaseDashboard from './pages/SalesPurchaseDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import NotesReminders from './pages/NotesReminders';
import WorkOrders from './pages/WorkOrders';
import TechnicianKPI from './pages/TechnicianKPI';
import Scan from './pages/Scan';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import FinanceDashboard from './pages/FinanceDashboard';
import Inspections from './pages/Inspections';
import Housekeeping from './pages/Housekeeping';
import Security from './pages/Security';
import JobOrders from './pages/JobOrders';
import DemoCtaModal from './components/DemoCtaModal';
import InspectionDetail from './pages/InspectionDetail';
import ChecklistTemplates from './pages/ChecklistTemplates';
import CreditDebitNotes from './pages/CreditDebitNotes';
import Statements from './pages/Statements';
import DemoTour from './pages/DemoTour';
import MaterialRequests from './pages/MaterialRequests';
import { CompanyProvider, CompanyContext } from './contexts/CompanyContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Bearer-token fallback for environments where the session cookie is blocked
// (e.g. third-party cookie restrictions on a custom production domain). The
// backend accepts both `session_token` cookie AND `Authorization: Bearer <token>`.
const SESSION_KEY = 'fmflexipro_session_token';
export function setSessionToken(token) {
  if (token) localStorage.setItem(SESSION_KEY, token);
  else localStorage.removeItem(SESSION_KEY);
}
export function getSessionToken() {
  return localStorage.getItem(SESSION_KEY);
}

// Inject Bearer header on every axios request when a token is stored.
axios.interceptors.request.use((config) => {
  const t = getSessionToken();
  if (t) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization && !config.headers.authorization) {
      config.headers.Authorization = `Bearer ${t}`;
    }
  }
  return config;
});

// Phase 26 — Globally surface TRIAL_EXPIRED (HTTP 402) responses so any menu click
// or background fetch turns into an actionable "your trial has ended" toast instead
// of a generic "Failed to fetch".
let lastTrialToastAt = 0;
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 402) {
      const detail = err.response.data?.detail;
      const code = typeof detail === 'object' ? detail?.code : null;
      if (code === 'TRIAL_EXPIRED') {
        const now = Date.now();
        if (now - lastTrialToastAt > 4000) {
          lastTrialToastAt = now;
          const msg = (typeof detail === 'object' ? detail.message : detail) || 'Your trial has ended.';
          try { toast.error(msg, { duration: 6000 }); } catch { /* toast may not be ready */ }
        }
      }
    }
    // Phase 30 — ONBOARDING_REQUIRED: push trial users to the wizard
    if (err?.response?.status === 403) {
      const detail = err.response.data?.detail;
      const code = typeof detail === 'object' ? detail?.code : null;
      if (code === 'ONBOARDING_REQUIRED' && !window.location.pathname.startsWith('/onboarding')) {
        window.location.replace('/onboarding');
      }
    }
    return Promise.reject(err);
  }
);

// Auth Context
export const AuthContext = React.createContext();

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check if there's a session_id in URL fragment
      const hash = window.location.hash;
      console.log('Checking auth, hash:', hash);
      
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        console.log('Found session_id in URL, processing...');
        await handleSession(sessionId);
        // Clean URL
        window.history.replaceState(null, '', window.location.pathname);
        setLoading(false);
        return;
      }

      // Check existing session
      console.log('Checking existing session...');
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      console.log('User authenticated:', response.data.email);
      
      // Redirect logic
      if (response.data.must_change_password && location.pathname !== '/change-password') {
        navigate('/change-password');
      } else if (location.pathname === '/' || location.pathname === '/login') {
        navigate('/dashboard');
      }
    } catch (error) {
      console.log('Auth check failed:', error.response?.status, error.message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSession = async (sessionId) => {
    try {
      console.log('Handling session with ID:', sessionId.substring(0, 20) + '...');
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        {
          withCredentials: true,
          // 15s hard timeout — if the OAuth verification hangs, surface it to
          // the user instead of leaving them on a loading spinner forever.
          timeout: 15000,
        }
      );
      console.log('Session created successfully:', response.data);
      if (response.data.session_token) setSessionToken(response.data.session_token);
      setUser(response.data.user);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Session creation failed:', error.response?.data, error.message);
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      const isTimeout = error.code === 'ECONNABORTED' || String(detail).toLowerCase().includes('timeout');
      toast.error(
        isTimeout
          ? 'Sign-in timed out. Please click "Continue with Google" again.'
          : `Sign-in failed: ${detail}`,
        { duration: 8000 }
      );
      // CRITICAL: send them back to /login so they can retry, don't leave the
      // spinner turning forever on /dashboard.
      setUser(null);
      setLoading(false);
      navigate('/login', { replace: true });
    }
  };

  const logout = async () => {
    // Always clear local token first so subsequent UI calls don't loop on a stale 401.
    const hadToken = !!getSessionToken();
    setSessionToken(null);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // 401 here is fine — it just means the server side session was already gone.
      if (!hadToken && error.response?.status !== 401) {
        toast.error('Logout failed');
      }
    }
    // Navigate BEFORE clearing user — otherwise ProtectedRoute re-renders first and
    // sends us to the wrong page. Route to /login so the next user can immediately sign in.
    navigate('/login', { replace: true });
    setUser(null);
    toast.success('Logged out successfully');
  };

  const loginDemo = async () => {
    try {
      const response = await axios.post(`${API}/auth/demo`, {}, { withCredentials: true });
      if (response.data.session_token) setSessionToken(response.data.session_token);
      setUser(response.data.user);
      toast.success('Welcome to the demo! (read-only)');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Demo login failed');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, logout, checkAuth, loginDemo }}>
      <ThemeProvider>
        <CompanyProvider>
          <LanguageBridge>{children}</LanguageBridge>
        </CompanyProvider>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}

// Bridges arabic_enabled from the active company → LanguageProvider
// On the landing page (no auth/company) we still allow Arabic preview so visitors
// can preview the AR translation before signup.
function LanguageBridge({ children }) {
  const { company, initialized } = React.useContext(CompanyContext);
  const { user } = React.useContext(AuthContext);
  const arabicAvailable = !user || !!(company && company.arabic_enabled);
  const ready = !user || initialized;
  return (
    <LanguageProvider arabicAvailable={arabicAvailable} ready={ready}>
      {children}
    </LanguageProvider>
  );
}

function ProtectedRoute({ children, allowMustChange = false, allowOnboarding = false }) {
  const { user, loading } = React.useContext(AuthContext);
  const { company } = React.useContext(CompanyContext);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  // Force password change before any other route
  if (user.must_change_password && !allowMustChange) {
    return <Navigate to="/change-password" replace />;
  }

  // Force onboarding for first-time company admins (except SUPERADMIN)
  const needsOnboarding =
    !allowOnboarding
    && user.role !== 'SUPERADMIN'
    && ['FULL_ACCESS', 'ADMIN', 'CEO'].includes(user.role)
    && company
    && company.onboarding_completed === false
    && location.pathname !== '/onboarding';

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Wrap children in ErrorBoundary so any unexpected runtime crash shows
  // a recoverable error card instead of a blank page (production safety net).
  return <ErrorBoundary>{children}</ErrorBoundary>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster richColors position="top-right" />
          <DemoCtaModal />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/demo-tour" element={<DemoTour />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute allowMustChange={true}>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-work"
              element={
                <ProtectedRoute>
                  <TechnicianDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/buildings"
              element={
                <ProtectedRoute>
                  <Buildings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/buildings/:id"
              element={
                <ProtectedRoute>
                  <BuildingDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contacts"
              element={<ProtectedRoute><Contacts /></ProtectedRoute>}
            />
            <Route
              path="/attendance"
              element={<ProtectedRoute><Attendance /></ProtectedRoute>}
            />
            <Route
              path="/schedules"
              element={<ProtectedRoute><Schedules /></ProtectedRoute>}
            />
            <Route
              path="/notices"
              element={<ProtectedRoute><Notices /></ProtectedRoute>}
            />
            <Route
              path="/sops"
              element={<ProtectedRoute><SOPs /></ProtectedRoute>}
            />
            <Route
              path="/vault"
              element={<ProtectedRoute><DocumentVault /></ProtectedRoute>}
            />
            <Route
              path="/workflows"
              element={<ProtectedRoute><Workflows /></ProtectedRoute>}
            />
            <Route
              path="/purchase-orders"
              element={<ProtectedRoute><PurchaseOrders /></ProtectedRoute>}
            />
            <Route
              path="/supplier-dashboard"
              element={<ProtectedRoute><SupplierDashboard /></ProtectedRoute>}
            />
            <Route
              path="/advanced-accounting"
              element={<ProtectedRoute><AdvancedAccounting /></ProtectedRoute>}
            />
            <Route
              path="/quotations"
              element={<ProtectedRoute><Quotations /></ProtectedRoute>}
            />
            <Route
              path="/vouchers"
              element={<ProtectedRoute><Vouchers /></ProtectedRoute>}
            />
            <Route
              path="/ppm-calendar"
              element={<ProtectedRoute><PpmCalendar /></ProtectedRoute>}
            />
            <Route
              path="/procurement"
              element={<ProtectedRoute><Procurement /></ProtectedRoute>}
            />
            <Route
              path="/sales-purchase-dashboard"
              element={<ProtectedRoute><SalesPurchaseDashboard /></ProtectedRoute>}
            />
            <Route
              path="/superadmin"
              element={<ProtectedRoute><SuperAdminDashboard /></ProtectedRoute>}
            />
            <Route
              path="/my-notes"
              element={<ProtectedRoute><NotesReminders /></ProtectedRoute>}
            />
            <Route
              path="/reports"
              element={<ProtectedRoute><ServiceReports /></ProtectedRoute>}
            />
            <Route
              path="/approvals"
              element={<ProtectedRoute><Approvals /></ProtectedRoute>}
            />
            <Route
              path="/purchase-requests"
              element={<ProtectedRoute><PurchaseRequests /></ProtectedRoute>}
            />
            <Route
              path="/analytics"
              element={<ProtectedRoute><Analytics /></ProtectedRoute>}
            />
            <Route
              path="/settings"
              element={<ProtectedRoute><Settings /></ProtectedRoute>}
            />
            <Route path="/contact" element={<Contact />} />
            <Route
              path="/help"
              element={<ProtectedRoute><Help /></ProtectedRoute>}
            />
            <Route
              path="/assets"
              element={
                <ProtectedRoute>
                  <Assets />
                </ProtectedRoute>
              }
            />
            <Route
              path="/complaints"
              element={
                <ProtectedRoute>
                  <Complaints />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ppm-tasks"
              element={
                <ProtectedRoute>
                  <PPMTasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff"
              element={
                <ProtectedRoute>
                  <Staff />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers"
              element={
                <ProtectedRoute>
                  <Suppliers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchases"
              element={
                <ProtectedRoute>
                  <PurchaseManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock"
              element={
                <ProtectedRoute>
                  <StockManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/material-requests"
              element={
                <ProtectedRoute>
                  <MaterialRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants"
              element={
                <ProtectedRoute>
                  <Tenants />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificates"
              element={
                <ProtectedRoute>
                  <Certificates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/utilities"
              element={
                <ProtectedRoute>
                  <UtilityReadings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-insights"
              element={
                <ProtectedRoute>
                  <AIInsights />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies"
              element={
                <ProtectedRoute>
                  <CompanyManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/custom-reports"
              element={
                <ProtectedRoute>
                  <CustomReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/amc-contracts"
              element={
                <ProtectedRoute>
                  <AMCContracts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/provider-kpi"
              element={
                <ProtectedRoute>
                  <ServiceProviderKPI />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stock-movements"
              element={
                <ProtectedRoute>
                  <StockMovements />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={<ProtectedRoute><AuditLogs /></ProtectedRoute>}
            />
            <Route
              path="/backups"
              element={<ProtectedRoute><Backups /></ProtectedRoute>}
            />
            <Route
              path="/role-access"
              element={<ProtectedRoute><RoleAccess /></ProtectedRoute>}
            />
            <Route
              path="/inspections"
              element={<ProtectedRoute><Inspections /></ProtectedRoute>}
            />
            <Route
              path="/inspections/:id"
              element={<ProtectedRoute><InspectionDetail /></ProtectedRoute>}
            />
            <Route
              path="/checklist-templates"
              element={<ProtectedRoute><ChecklistTemplates /></ProtectedRoute>}
            />
            <Route
              path="/credit-debit-notes"
              element={<ProtectedRoute><CreditDebitNotes /></ProtectedRoute>}
            />
            <Route
              path="/statements"
              element={<ProtectedRoute><Statements /></ProtectedRoute>}
            />
            <Route
              path="/housekeeping"
              element={<ProtectedRoute><Housekeeping /></ProtectedRoute>}
            />
            <Route
              path="/security"
              element={<ProtectedRoute><Security /></ProtectedRoute>}
            />
            <Route
              path="/security/:tab"
              element={<ProtectedRoute><Security /></ProtectedRoute>}
            />
            <Route
              path="/job-orders"
              element={<ProtectedRoute><JobOrders /></ProtectedRoute>}
            />
            <Route
              path="/accounting"
              element={<ProtectedRoute><Accounting /></ProtectedRoute>}
            />
            <Route
              path="/hr"
              element={<ProtectedRoute><HR /></ProtectedRoute>}
            />
            <Route
              path="/onboarding"
              element={<ProtectedRoute allowOnboarding={true}><Onboarding /></ProtectedRoute>}
            />
            <Route
              path="/superadmin/onboard-company"
              element={<ProtectedRoute><SuperAdminOnboardCompany /></ProtectedRoute>}
            />
            <Route
              path="/pending-returns"
              element={<ProtectedRoute><PendingReturns /></ProtectedRoute>}
            />
            <Route
              path="/subscription"
              element={<ProtectedRoute><Subscription /></ProtectedRoute>}
            />
            <Route
              path="/work-orders"
              element={<ProtectedRoute><WorkOrders /></ProtectedRoute>}
            />
            <Route
              path="/technician-kpi"
              element={<ProtectedRoute><TechnicianKPI /></ProtectedRoute>}
            />
            <Route
              path="/scan"
              element={<ProtectedRoute><Scan /></ProtectedRoute>}
            />
            <Route
              path="/executive"
              element={<ProtectedRoute><ExecutiveDashboard /></ProtectedRoute>}
            />
            <Route
              path="/finance"
              element={<ProtectedRoute><FinanceDashboard /></ProtectedRoute>}
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
export { API };